import useForage from 'contexts/ForageContext/useForage';
import { punctuateText } from 'utils/globalUtils';
import twclsx from 'utils/twclsx';

/**
 * Custom error messages for Forage error codes, which are often not user-friendly.
 * @type {Object.<string, string|function>}
 */
const EBT_ERROR_MESSAGES = {
    ebt_error_14: 'Invalid card number, please enter a valid card number in "My Profile" and try again.',
    ebt_error_43: 'Card was lost or stolen and cannot be processed.',
    ebt_error_51: (error) => {
        const balance = error?.availableSNAPBalance;
        if (balance != null) {
            const numBalance = Number(balance);
            if (!Number.isNaN(numBalance)) {
                return `Insufficient EBT funds. Your current SNAP balance is $${numBalance.toFixed(2)}. Please reduce your EBT amount and try again.`;
            }
        }
        return 'Insufficient EBT funds. Please reduce your EBT amount and try again.';
    },
    ebt_error_54: 'Your card has expired. Please update your card information in "My Profile" and try again.',
    ebt_error_55: 'Invalid PIN. Please call the number on the back of your EBT card for help if this issue persists.',
    ebt_error_62:
        'This card is restricted and cannot be processed. Please call the number on the back of your EBT card for help.',
    expired_session_token: 'Your session has expired.',
};

/**
 * Checks if the error is a Forage session expired error
 * @param {Object} error - The error object to check
 * @param {string} error.code - The error code
 * @returns {boolean} True if the error is a session expired error
 */
function isForageSessionExpiredError(error) {
    return error?.code === 'expired_session_token';
}

/**
 * Gets the appropriate error message for an EBT error
 * @param {Object} error - The error object
 * @param {string} [error.code] - The error code (from Forage SDK)
 * @param {string} [error.errorCode] - The error code (from API)
 * @returns {string|undefined} The error message if found, undefined otherwise
 */
function getEBTErrorMessage(error) {
    const code = error?.code || error?.errorCode;
    const errorMessage = EBT_ERROR_MESSAGES[code];

    if (typeof errorMessage === 'function') {
        return errorMessage(error);
    }

    return errorMessage;
}

/**
 * A reusable component for displaying EBT error messages
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The error message content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} The error message component
 */
function EBTErrorMessage({ children, className }) {
    return (
        <div className={twclsx('text-body-sm mt-10 text-error', className)} data-testid="ebt-error-message">
            {children}
        </div>
    );
}

/**
 * Component for displaying Forage session expired errors with a refresh option
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.clearErrors] - Function to clear errors when refreshing session
 * @returns {JSX.Element} The session expired error component
 */
function ForageSessionExpiredError({ className, clearErrors }) {
    const { refreshSession } = useForage();

    function handleRefreshSession() {
        clearErrors?.();
        refreshSession();
    }

    return (
        <p
            className={twclsx('text-body-sm mt-10 text-center text-error', className)}
            data-testid="ebt-session-expired-error"
        >
            {EBT_ERROR_MESSAGES.expired_session_token}{' '}
            <button
                type="button"
                onClick={handleRefreshSession}
                className="font-grotesk-bold underline hover:no-underline"
            >
                Start a new session
            </button>{' '}
            to continue.
        </p>
    );
}

/**
 * Main component for handling EBT form submission errors
 * - Rewrites some Forage error messages to be more user-friendly.
 * - Displays a session expired error message if the error is a session expired error.
 * - Displays a fallback error message if there is no error message.
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.clearErrors] - Function to clear errors
 * @param {Object} [props.error] - The error object to display
 * @param {string} [props.error.code] - The error code
 * @param {string} [props.error.message] - The error message
 * @returns {JSX.Element|null} The error component or null if no error
 */
function EBTFormSubmissionError({ className, clearErrors, error }) {
    if (!error) {
        return null;
    }

    if (isForageSessionExpiredError(error)) {
        return <ForageSessionExpiredError className={className} clearErrors={clearErrors} />;
    }

    const ebtErrorMessage = getEBTErrorMessage(error);

    const errorDetails =
        ebtErrorMessage ||
        error?.message ||
        'We&apos;re sorry, but an error occurred during submission. Please try again.';

    return (
        <EBTErrorMessage className={className}>
            <p>{punctuateText(errorDetails)}</p>
        </EBTErrorMessage>
    );
}

export { isForageSessionExpiredError, EBTFormSubmissionError, EBTErrorMessage, getEBTErrorMessage };

export default ForageSessionExpiredError;
